import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  UNSAFE_withErrorBoundaryProps,
} from "react-router-dom";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  NumericEntryField,
  PercentageField,
} from "../../components/common/CustomFields";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQWorkOrder,
  useTSQWorkOrderPalletList,
  useTSMCreateWorkOrder,
  useTSMUpdateWorkOrder,
  useTSMBuildWorkOrder,
  useTSMCompleteWO,
  useTSMVoidWO,
  useTSMStatusUpdateWO,
} from "../../hooks/useTSWorkOrder";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQLotByNum } from "../../hooks/useTSLots";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQLocationList } from "../../hooks/useTSLocations";
import { useTSQBomCompsByRev, useTSQBOMRevList } from "../../hooks/useTSBOMs";
import {
  ProdRevCreate,
  WOCompConsumption,
  WorkOrderFull,
  WorkOrderCreate,
  WorkOrderUpdate,
  WOCompCreate,
  WOCompUpdate,
} from "../../types/workOrderTypes";
import {
  ItemOption,
  BomRevOption,
  UnitOption,
  LocationOption,
  WOOption,
  YupTypedSelectOption,
  TypedSelectOption,
} from "../../types/common/selectOptions";
import { ItemFull } from "../../types/itemTypes";
import { UOMUnitFull } from "../../types/uomTypes";
import { LocationFull } from "../../types/locationTypes";
import { BOMRevFull } from "../../types/bomTypes";
import { InvPosFull } from "../../types/invPosTypes";
import ContextPopover from "../../components/common/CustomPopoverContext";
import { useTSQInvPositionListItem } from "../../hooks/useTSInvPosition";
import stringUtils from "../../utils/stringUtils";
import OrderStatusActions from "../../components/common/OrderStatusActionsPanel";
import statusUtils from "../../utils/statusUtils";
import { useTSQTGCMList } from "../../hooks/useTSTGCM";

const calcRequiredValue = (target: number, per: number, offset: number) => {
  return (target / offset) * per;
};

const today = new Date();
const endDate = (() => {
  let temp = new Date(today);
  temp.setDate(temp.getDate() + 7);
  return temp;
})();

type AItemOption = ItemOption<{
  num: string;
  type_id: string;
  expires: boolean;
}>;

type AUnitOption = UnitOption<{
  type_id: string;
  conversion: number;
}>;

type ABomRevOption = BomRevOption<{}>;

type ALocationOption = LocationOption<{}>;

type APalletOption = WOOption<{ build_complete: boolean }>;

type ATGCMOption = TypedSelectOption<"record", {}>;

interface WOCompData {
  line_id: string | null;
  line: number;
  quantity_per: number;
  quantity_required: number;
  quantity_allocated: number;
  quantity_offset: number;
  bom_rev_id: string | null;
  notes: string | null;
  component_item: AItemOption | null;
  uom_unit: AUnitOption | null;
  is_new: boolean;
  is_delete: boolean;
}
type WOCompEditData = Partial<Omit<WOCompData, "line_id" | "line" | "is_new">>;

const defaultCompData: WOCompData = {
  line_id: null,
  line: 999,
  quantity_per: 0,
  quantity_required: 0,
  quantity_allocated: 0,
  quantity_offset: 1,
  bom_rev_id: null,
  notes: null,
  component_item: null,
  uom_unit: null,
  is_new: true,
  is_delete: false,
};

const YupCompData = yup.object({
  id: yup.string().nullable(),
  line: yup.number().required(),
  quantity_per: yup
    .number()
    .required("The quantity per for this Component line is required"),
  quantity_required: yup
    .number()
    .required("The total quantity needed for this Component line is required"),
  quantity_allocated: yup.number(),
  quantity_offset: yup
    .number()
    .required("The offset value for this Component line is required"),
  bom_rev_id: yup.string().nullable(),
  notes: yup.string().nullable(),
  component_item: YupTypedSelectOption.required(
    "The Component Item is required for this line",
  ),
  uom_unit: YupTypedSelectOption.required(
    "The UOM of the Component Item is required for this line",
  ),
  is_new: yup.boolean().required(),
  is_delete: yup.boolean().required(),
});

interface WOFormData {
  form_mode: string;
  wo_number: string;
  wo_pallet_name: string | null;
  wo_pallet_lot: APalletOption | null;
  wo_is_rework: boolean;
  wo_is_planned_deviation: boolean;
  wo_is_eng_build: boolean;
  wo_is_bioburden: boolean;
  wo_item: AItemOption | null;
  wo_uom_unit: AUnitOption | null;
  wo_quantity: number;
  wo_location: ALocationOption | null;
  wo_bom_revision: ABomRevOption | null;
  wo_target_revision: string;
  wo_planned_start_date: string;
  wo_planned_completion_date: string;
  wo_notes: string | null;
  wo_components: WOCompData[];
  wo_updated_components: WOCompEditData[];
  pr_completed: boolean;
  pr_quantity_produced: number;
  pr_built_revision: string;
  pr_actual_start_date: string;
  pr_actual_completion_date: string;
  pr_expiration_date: string | null;
  pr_right_first_time: boolean;
  pr_rework: boolean;
  pr_planned_deviation: boolean;
  pr_notes: string | null;
  pr_linked_tgcm: ATGCMOption[];
}

type WOFormEditData = Partial<
  Omit<
    WOFormData,
    | "wo_components"
    | "wo_updated_components"
    | "pr_completed"
    | "pr_quantity_produced"
    | "pr_built_revision"
    | "pr_actual_start_date"
    | "pr_actual_completion_date"
    | "pr_expiration_date"
    | "pr_right_first_time"
    | "pr_rework"
    | "pr_planned_deviation"
    | "pr_notes"
  >
>;

const defaultWoFormData: WOFormData = {
  form_mode: "create",
  wo_number: "",
  wo_pallet_name: null,
  wo_pallet_lot: null,
  wo_is_rework: false,
  wo_is_planned_deviation: false,
  wo_is_eng_build: false,
  wo_is_bioburden: false,
  wo_item: null,
  wo_uom_unit: null,
  wo_quantity: 0,
  wo_location: null,
  wo_bom_revision: null,
  wo_target_revision: "",
  wo_planned_start_date: today.toISOString(),
  wo_planned_completion_date: endDate.toISOString(),
  wo_notes: null,
  wo_components: [],
  wo_updated_components: [],
  pr_completed: false,
  pr_quantity_produced: 0,
  pr_built_revision: "",
  pr_actual_start_date: today.toISOString(),
  pr_actual_completion_date: today.toISOString(),
  pr_expiration_date: null,
  pr_right_first_time: false,
  pr_rework: false,
  pr_planned_deviation: false,
  pr_notes: null,
  pr_linked_tgcm: [],
};

const LotLookupDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [lotLookupNum, setLotLookupNum] = useState<string>("");
  const [errText, setErrText] = useState<string | null>(null);
  const [shouldSearch, setShouldSearch] = useState<boolean>(false);
  const {
    data: thisLotData,
    isLoading: thisLotLoading,
    error: thisLotError,
  } = useTSQLotByNum(lotLookupNum, !!lotLookupNum && shouldSearch);

  useEffect(() => {
    if (thisLotData) {
      setShouldSearch(false);
    }
  }, [thisLotData]);

  const handleSearchButton = () => {
    if (!lotLookupNum) {
      setErrText("Please enter a valid lot number before searching.");
    } else {
      setErrText(null);
      setShouldSearch(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Lot Information Lookup</DialogTitle>
      <DialogContent>
        <Stack direction={"column"} gap={1} p={3}>
          {errText && (
            <Alert severity="error" onClose={() => setErrText(null)}>
              {errText}
            </Alert>
          )}
          <Box display="flex" gap={1}>
            <TextField
              value={lotLookupNum}
              onChange={(e) => setLotLookupNum(e.target.value)}
              label="Search By Lot Number"
              fullWidth
            />
            <Button
              variant="contained"
              color="info"
              onClick={handleSearchButton}
              disabled={!lotLookupNum}
            >
              Search For Lot
            </Button>
          </Box>
          {thisLotLoading ? (
            <Box display="flex" gap={1}>
              <Alert severity="info">Loading Lot...</Alert>
              <CircularProgress />
            </Box>
          ) : !!thisLotData ? (
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gridTemplateRows="auto"
              columnGap={1}
              rowGap={1}
              p={1}
              sx={{
                background: "#46bbffff",
                border: "1px solid black",
                borderRadius: 8,
              }}
            >
              <Box
                display="flex"
                sx={{ gridRow: 1, gridColumn: 1 }}
                textAlign={"left"}
              >
                <Typography variant="body1">{`Lot Number: ${thisLotData.lot_number}`}</Typography>
              </Box>
              <Box
                display="flex"
                sx={{ gridRow: 1, gridColumn: 2 }}
                textAlign={"left"}
              >
                <Typography variant="body1">{`Item: ${thisLotData.item.number}`}</Typography>
              </Box>
              <Box
                display="flex"
                sx={{ gridRow: 2, gridColumn: 1 }}
                textAlign={"left"}
              >
                <Typography variant="body1">{`Released Date: ${
                  thisLotData.released_date || "N/A"
                }`}</Typography>
              </Box>
              <Box
                display="flex"
                sx={{ gridRow: 2, gridColumn: 2 }}
                textAlign={"left"}
              >
                <Typography variant="body1">{`Released Rev: ${
                  thisLotData.released_revision || "N/A"
                }`}</Typography>
              </Box>
              <Box
                display="flex"
                sx={{ gridRow: 3, gridColumn: 1 }}
                textAlign={"left"}
              >
                <Typography variant="body1">{`Expiration Date: ${
                  thisLotData.expiration_date || "N/A"
                }`}</Typography>
              </Box>
            </Box>
          ) : !lotLookupNum ? (
            <Typography variant="h4">
              Please enter a lot number to search by...
            </Typography>
          ) : shouldSearch ? (
            <Typography variant="h4">{`Could not locate Lot with number: ${lotLookupNum}. Please try searching again or notify IT via the Bug Report Form and attempt to create the missing lot yourself.`}</Typography>
          ) : (
            <></>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const CompDialog: React.FC<{
  open: boolean;
  buildQuant: number;
  itemOpts: AItemOption[];
  unitOpts: AUnitOption[];
  prevCompData: WOCompData | null;
  prevCompEdits: WOCompEditData | null;
  onSubmit: (data: WOCompData, edits: WOCompEditData) => void;
  onCancel: () => void;
  onCompItemSelect: (newValue: AItemOption | null) => boolean;
}> = ({
  open,
  buildQuant,
  itemOpts,
  unitOpts,
  prevCompData,
  prevCompEdits,
  onSubmit,
  onCancel,
  onCompItemSelect,
}) => {
  const [thisCompData, setThisCompData] = useState<WOCompData>(defaultCompData);
  const [thisCompEdits, setThisCompEdits] = useState<WOCompEditData>({});
  const [thisDialogErrors, setThisDialogErrors] = useState<{
    item?: string;
    unit?: string;
    quant_per?: string;
    quant_offset?: string;
  }>({
    item: undefined,
    unit: undefined,
    quant_per: undefined,
    quant_offset: undefined,
  });

  useEffect(() => {
    if (open) {
      if (prevCompData) {
        setThisCompEdits(prevCompEdits ? prevCompEdits : {});
        setThisCompData(prevCompData);
      } else {
        setThisCompData(defaultCompData);
        setThisCompEdits({});
      }
    } else {
      setThisCompData(defaultCompData);
      setThisCompEdits({});
      setThisDialogErrors({
        item: undefined,
        unit: undefined,
        quant_per: undefined,
        quant_offset: undefined,
      });
    }
  }, [open, prevCompData, prevCompEdits]);

  const handleSubmit = () => {
    const newErrs: {
      item?: string;
      unit?: string;
      quant_per?: string;
      quant_offset?: string;
    } = {
      item: undefined,
      unit: undefined,
      quant_per: undefined,
      quant_offset: undefined,
    };
    if (thisCompData.component_item === null) {
      newErrs.item = "Component Item is required";
    }
    if (thisCompEdits.component_item) {
      if (onCompItemSelect(thisCompEdits.component_item) === false) {
        newErrs.item =
          "Component Item is already selected on another line. You cannot have multiple lines with the same component.";
      }
    } else {
      if (onCompItemSelect(thisCompData.component_item) === false) {
        newErrs.item =
          "Component Item is already selected on another line. You cannot have multiple lines with the same component.";
      }
    }
    if (thisCompData.uom_unit === null) {
      newErrs.unit = "Component UOM is required";
    }
    if (thisCompData.quantity_per <= 0) {
      newErrs.quant_per = "Quantity Per must be greater than 0";
    }
    if (thisCompData.quantity_offset < 1) {
      newErrs.quant_offset =
        "Quantity Offset must be greater than or equal to 1";
    }
    if (Object.values(newErrs).some((v) => !!v)) {
      setThisDialogErrors(newErrs);
      return;
    } else {
      setThisDialogErrors({
        item: undefined,
        unit: undefined,
        quant_per: undefined,
        quant_offset: undefined,
      });
    }
    onSubmit(thisCompData, thisCompEdits);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {thisCompData.is_new ? "Add New Component Line" : "Edit Component Line"}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={1} p={3}>
          <Autocomplete
            value={
              thisCompEdits.component_item === undefined
                ? thisCompData.component_item
                : thisCompEdits.component_item
            }
            options={itemOpts}
            onChange={(e, v) => {
              if (thisCompData.is_new) {
                setThisCompData((prev) => ({
                  ...prev,
                  component_item: v,
                  uom_unit:
                    unitOpts.find(
                      (o) =>
                        o.value.type_id === v?.value.type_id &&
                        o.value.conversion === 1,
                    ) || null,
                  quantity_per: 0,
                  quantity_offset: 1,
                  quantity_required: 0,
                }));
              } else {
                setThisCompEdits((prev) => ({
                  ...prev,
                  component_item: v,
                  uom_unit:
                    unitOpts.find(
                      (o) =>
                        o.value.type_id === v?.value.type_id &&
                        o.value.conversion === 1,
                    ) || null,
                  quantity_per: 0,
                  quantity_offset: 1,
                  quantity_required: 0,
                }));
              }
            }}
            disabled={thisCompData.bom_rev_id !== null}
            fullWidth
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value.id === val.value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Component"
                error={!!thisDialogErrors.item}
                helperText={
                  thisDialogErrors.item
                    ? thisDialogErrors.item
                    : thisCompData.bom_rev_id !== null
                      ? "Component Line Items Sourced From BOMs cannot be edited."
                      : undefined
                }
              />
            )}
          />
          <Autocomplete
            value={thisCompData.uom_unit}
            options={unitOpts.filter(
              (o) =>
                o.value.type_id === thisCompData.component_item?.value.type_id,
            )}
            disabled={thisCompData.bom_rev_id !== null}
            fullWidth
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value.id === val.value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Unit"
                error={!!thisDialogErrors.unit}
                helperText={
                  thisDialogErrors.unit
                    ? thisDialogErrors.unit
                    : thisCompData.bom_rev_id !== null
                      ? "Component Line UOMs Sourced From BOMs cannot be edited."
                      : undefined
                }
              />
            )}
          />
          <NumericEntryField
            label="Quantity Per"
            value={
              thisCompEdits.quantity_per === undefined
                ? thisCompData.quantity_per
                : thisCompEdits.quantity_per
            }
            onChange={(v) => {
              if (thisCompData.is_new) {
                setThisCompData((prev) => ({
                  ...prev,
                  quantity_per: v,
                  quantity_required: (v * buildQuant) / prev.quantity_offset,
                }));
              } else {
                setThisCompEdits((prev) => ({
                  ...prev,
                  quantity_per: v,
                  quantity_required:
                    (v * buildQuant) /
                    (prev.quantity_offset === undefined
                      ? thisCompData.quantity_offset
                      : prev.quantity_offset),
                }));
              }
            }}
            min={0}
            decimalPlaces={2}
            isCurrency={false}
            textFieldProps={{
              fullWidth: true,
              error: !!thisDialogErrors.quant_per,
              helperText: thisDialogErrors.quant_per,
            }}
          />
          <NumericEntryField
            label="Quantity Offset"
            value={
              thisCompEdits.quantity_offset === undefined
                ? thisCompData.quantity_offset
                : thisCompEdits.quantity_offset
            }
            onChange={(v) => {
              if (thisCompData.is_new) {
                setThisCompData((prev) => ({
                  ...prev,
                  quantity_offset: v,
                  quantity_required: (prev.quantity_per * buildQuant) / v,
                }));
              } else {
                setThisCompEdits((prev) => ({
                  ...prev,
                  quantity_offset: v,
                  quantity_required:
                    ((prev.quantity_per === undefined
                      ? thisCompData.quantity_per
                      : prev.quantity_per) *
                      buildQuant) /
                    v,
                }));
              }
            }}
            min={0}
            decimalPlaces={2}
            isCurrency={false}
            textFieldProps={{
              fullWidth: true,
              error: !!thisDialogErrors.quant_offset,
              helperText:
                thisDialogErrors.quant_offset ||
                "If you don't know what to put here, just leave it at 1",
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="success" onClick={handleSubmit}>
          Save
        </Button>
        <Button variant="outlined" color="error" onClick={onCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WOCompLine: React.FC<{
  index: number;
  formMode: string;
  compData: WOCompData;
  compEdits: WOCompEditData;
  buildItem: AItemOption | null;
  buildUnit: AUnitOption | null;
  handleRemoveComp: (index: number, data: WOCompData) => void;
  handleEditComp: (
    index: number,
    data: WOCompData,
    edits: WOCompEditData | null,
  ) => void;
}> = ({
  index,
  formMode,
  compData,
  compEdits,
  buildItem,
  buildUnit,
  handleRemoveComp,
  handleEditComp,
}) => {
  const compItemDisplay = useMemo(() => {
    if (compEdits?.component_item) {
      const parts = compEdits.component_item.label.split(" - ");
      return (
        <Box display="flex" gap={0.5} alignItems="center">
          <Stack direction="column" gap={0.5}>
            <Typography variant="body1">{parts[0]}</Typography>
            <Typography variant="body2">{parts[1]}</Typography>
          </Stack>
          <EditIcon color="info" />
        </Box>
      );
    } else {
      const parts = compData.component_item?.label.split(" - ") || [
        "UNKNOWN PART NUMBER",
        "UNKNOWN PART NAME",
      ];
      return (
        <Stack direction="column" gap={0.5}>
          <Typography variant="body1">{parts[0]}</Typography>
          <Typography variant="body2">{parts[1]}</Typography>
        </Stack>
      );
    }
  }, [compEdits, compData]);

  const compQuantDisplay = useMemo(() => {
    const uom = (() => {
      if (compEdits?.uom_unit) {
        return (
          <Box display="flex" gap={0.5}>
            <Typography variant="body1">{compEdits.uom_unit.label}</Typography>
            <EditIcon color="info" />
          </Box>
        );
      } else {
        return (
          <Typography variant="body1">
            {compData.uom_unit?.label || "UNKNOWN UOM"}
          </Typography>
        );
      }
    })();
    const per = (() => {
      if (compEdits?.quantity_per) {
        return (
          <Box display="flex" gap={0.5}>
            <Typography variant="body1">{`${compEdits.quantity_per}x`}</Typography>
            <EditIcon color="info" />
          </Box>
        );
      } else {
        return (
          <Typography variant="body1">{`${compData.quantity_per}x`}</Typography>
        );
      }
    })();
    const offset = (() => {
      if (compEdits?.quantity_offset) {
        return (
          <Box display="flex" gap={0.5}>
            <Typography variant="body2">{`Per ${compEdits.quantity_offset}x ${buildUnit?.label} of ${buildItem?.value.num}`}</Typography>
            <EditIcon color="info" />
          </Box>
        );
      } else {
        return (
          <Typography variant="body2">{`Per ${compData.quantity_offset}x ${buildUnit?.label} of ${buildItem?.value.num}`}</Typography>
        );
      }
    })();
    const total = (() => {
      if (compEdits?.quantity_required) {
        return (
          <Box display="flex" gap={0.5}>
            <Typography variant="body1">{`Total: ${compEdits.quantity_required}x`}</Typography>
            <EditIcon color="info" />
          </Box>
        );
      } else {
        return (
          <Typography variant="body1">{`Total: ${compData.quantity_required}x`}</Typography>
        );
      }
    })();
    return (
      <Stack direction="column" gap={0.5}>
        <Box display="flex" gap={0.5}>
          {per}
          {uom}
        </Box>
        {offset}
        <Box display="flex" gap={0.5}>
          {total}
          {uom}
        </Box>
      </Stack>
    );
  }, [buildItem, buildUnit, compData, compEdits]);
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
          <Box display="flex" gap={0.5} alignItems="center">
            #{index + 1} {compData.is_delete && <DeleteIcon color="error" />}{" "}
            {compData.is_new && <StarIcon color="success" />}
          </Box>
        </Typography>
      </TableCell>
      <TableCell align={"left"} sx={{ wordWrap: "normal", maxWidth: "10%" }}>
        {compItemDisplay}
      </TableCell>
      <TableCell align="left">{compQuantDisplay}</TableCell>
      <TableCell>
        <Typography variant="body1">{`${compData.quantity_allocated}x ${
          compData.uom_unit?.label || "UNKNOWN COMPONENT UNIT"
        }`}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body1" noWrap={false}>
          {compEdits?.notes || compData.notes || "No notes provided"}
        </Typography>
        {compEdits?.notes && <EditIcon color="info" />}
      </TableCell>
      {(formMode === "create" || formMode === "edit") && (
        <TableCell>
          <Stack direction="column" gap={1}>
            {formMode === "create" && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleRemoveComp(index, compData)}
                size="small"
                startIcon={<DeleteIcon />}
              >
                {compData.is_new ? "Remove Component" : "Mark For Deletion"}
              </Button>
            )}
            {(formMode === "create" || formMode === "edit") &&
              compData.quantity_allocated === 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleEditComp(index, compData, compEdits)}
                  size="small"
                  startIcon={<EditIcon />}
                >
                  Edit Component
                </Button>
              )}
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
};

export const PageWorkOrderForm: React.FC = () => {
  // console.log("Enter Form",Date.now())
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  // const searchParams = new URLSearchParams(location.search);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [recId, setRecId] = useState<string | null>(id);
  const formMode = searchParams.get("mode");
  if (!formMode) {
    throw new Error("Unable to determine form mode");
  }
  const refFormMode = useRef<string>(formMode);
  const refOrigData = useRef<WorkOrderFull | null>(null);
  const refRenderCount = useRef<number>(0);

  // Form State
  const [error, setError] = useState<string | null>(null);
  const [formInit, setFormInit] = useState<boolean>(formMode === "create");

  const [woFormEdits, setWoFormEdits] = useState<WOFormEditData>({});
  const [editsDisabled, setEditsDisabled] = useState<boolean>(true);

  const [overrideWOGen, setOverrideWOGen] = useState<boolean>(false);
  const [origWONum, setOrigWONum] = useState<string>("");

  const [overrideTargetRev, setOverrideTargetRev] = useState<boolean>(false);
  const [searchForComps, setSearchForComps] = useState<boolean>(false);

  // Form Data
  const createWOMutation = useTSMCreateWorkOrder();
  const updateWOMutation = useTSMUpdateWorkOrder();
  const buildWOMutation = useTSMBuildWorkOrder();
  const completeWOMutation = useTSMCompleteWO();
  const voidWOMutation = useTSMVoidWO();
  const statusUpdateMutation = useTSMStatusUpdateWO();

  // Validation schema
  const WorkOrderSchema = yup.object({
    form_mode: yup.string().required().oneOf(["create", "edit", "build"]),
    wo_number: yup.string(),
    wo_pallet_name: yup.string().when("wo_item", {
      is: (item: AItemOption | null) =>
        ["MSP-1782", "MSP-2388"].includes(item?.value.num || ""),
      then: (schema) =>
        schema.required(
          "Pallet Name is required when Item is 'MSP-1782'/'MSP-2388'",
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    wo_pallet_lot: YupTypedSelectOption.nullable().test({
      name: "pallet-lot",
      test: (value, context) => {
        if (
          !context.parent.wo_is_bioburden &&
          context.parent.wo_item?.label.includes("FGS") &&
          !value
        ) {
          return context.createError({
            path: "wo_pallet_lot",
            message:
              "Pallet Lot is Required when creating a Non-BioBurden FGS Work Order",
          });
        }
        return true;
      },
    }),
    wo_is_rework: yup.boolean().required(),
    wo_is_planned_deviation: yup.boolean().required(),
    wo_is_eng_build: yup.boolean().required(),
    wo_is_bioburden: yup.boolean().required(),
    wo_item: YupTypedSelectOption.required("Item being built is required"),
    wo_uom_unit: YupTypedSelectOption.required(
      "UOM for Item being built is required",
    ),
    wo_quantity: yup
      .number()
      .moreThan(0, "A Quantity greater than 0 is required"),
    wo_location: YupTypedSelectOption.required(
      "Location of Work Order is required",
    ),
    wo_bom_revision: YupTypedSelectOption.nullable(),
    wo_target_revision: yup
      .string()
      .required("Target Build Revision is required"),
    wo_planned_start_date: yup
      .date()
      .required("Planned Start Date is required"),
    wo_planned_completion_date: yup
      .date()
      .required("Planned Completion Date is required"),
    wo_notes: yup.string().nullable().defined(),
    wo_components: yup.array(YupCompData),
    pr_quantity_produced: yup.number().when("form_mode", {
      is: (form_mode: string) => form_mode === "build",
      then: (schema) =>
        schema.required().moreThan(0, "Quantity Produced must be more than 0"),
    }),
    pr_built_revision: yup.string().when("form_mode", {
      is: (form_mode: string) => form_mode === "build",
      then: (schema) =>
        schema.required().not([""], "The Revision Built is required"),
    }),
    pr_actual_start_date: yup.string().when("form_mode", {
      is: (form_mode: string) => form_mode === "build",
      then: (schema) => schema.required("The Actual Start Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    pr_actual_completion_date: yup.string().when("form_mode", {
      is: (form_mode: string) => form_mode === "build",
      then: (schema) =>
        schema.required("The Actual Completion Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    pr_expiration_date: yup
      .string()
      .nullable()
      .when(["form_mode", "wo_item"], {
        is: (form_mode: string, item: AItemOption | null) =>
          form_mode === "build" && item?.value.expires === true,
        then: (schema) =>
          schema.required("Expiration Date is required if the Item expires"),
      }),
    pr_right_first_time: yup.boolean().required(),
    pr_rework: yup.boolean().required(),
    pr_planned_deviation: yup.boolean().required(),
    pr_notes: yup.string().nullable(),
    pr_linked_tgcm: yup
      .array(YupTypedSelectOption.required().defined())
      .defined()
      .test({
        name: "tgcm-test",
        test: (value, context) => {
          const targetQuant = context.parent.pr_quantity_produced;
          const targetItem = context.parent.wo_item;
          const formMode = context.parent.form_mode;
          if (
            value.length !== targetQuant &&
            targetItem.value.num === "MSP-1393" &&
            formMode === "build"
          ) {
            return context.createError({
              message: `Must Link ${targetQuant}x TGCMs to this Build`,
              path: "pr_linked_tgcm",
            });
          }
          return true;
        },
      }),
  });

  const {
    data: thisWOData,
    isLoading: thisWOLoading,
    error: thisWOError,
    refetch: thisWORefetch,
  } = useTSQWorkOrder(recId || "", !!recId && formMode !== "create");

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemList({
    active_status: "active",
    filters: [
      [
        {
          field: "item_type",
          operator: "not_in",
          value: ["SUPPLY", "SERVICE"],
        },
      ],
    ],
  });
  const allItemOpts: AItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      return itemsResponse.items.map((i) => ({
        label: `${i.number} - ${i.name}`,
        type: "item",
        value: {
          id: i.id,
          num: i.number,
          type_id: i.uom_type.id,
          expires: i.item_expires,
        },
      }));
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQUOMUnitList({
    active_status: "active",
    filters: [[]],
  });
  const allUnitOpts: AUnitOption[] = useMemo(() => {
    if (unitsResponse?.items) {
      return unitsResponse.items.map((u) => ({
        label: u.unit_symbol,
        type: "unit",
        value: {
          id: u.id,
          type_id: u.uom_type_id,
          conversion: parseFloat(u.conversion_factor),
        },
      }));
    } else {
      return [];
    }
  }, [unitsResponse]);

  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
  } = useTSQLocationList({
    active_status: "active",
    filters: [[{ field: "top_location_id", operator: "==", value: null }]],
  });
  const locOpts: ALocationOption[] = useMemo(() => {
    if (locsResponse?.items) {
      return locsResponse.items.map((l) => ({
        label: l.name,
        type: "location",
        value: {
          id: l.id,
        },
      }));
    } else {
      return [];
    }
  }, [locsResponse]);

  const {
    data: palletsResponse,
    isLoading: palletsLoading,
    error: palletsError,
  } = useTSQWorkOrderPalletList("work_order_page", true);
  const allPalletOpts: APalletOption[] = useMemo(() => {
    if (palletsResponse?.items) {
      return palletsResponse.items.map((p) => ({
        label: `${p.number} - ${p.pallet_name}`,
        group: p.item_name,
        type: "workorder",
        value: {
          id: p.id,
          build_complete: p.build_status === "COMPLETE",
        },
      }));
    }
    return [];
  }, [palletsResponse]);

  const dataLoading =
    thisWOLoading ||
    itemsLoading ||
    unitsLoading ||
    locsLoading ||
    palletsLoading;

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
    setFocus,
  } = useForm<WOFormData>({
    resolver: yupResolver(WorkOrderSchema) as any,
    defaultValues: defaultWoFormData,
  });

  const [
    watchedWONum,
    watchedPRComplete,
    watchedBuildQuant,
    watchedBuildItem,
    watchedBuildUnit,
    watchedBomRev,
    watchedTargetRev,
    watchedEngBuild,
    watchedPalletLot,
  ] = watch([
    "wo_number",
    "pr_completed",
    "wo_quantity",
    "wo_item",
    "wo_uom_unit",
    "wo_bom_revision",
    "wo_target_revision",
    "wo_is_eng_build",
    "wo_pallet_lot",
  ]);

  const {
    fields: components,
    append: appendComp,
    remove: removeComp,
    update: updateComp,
    replace: replaceComp,
  } = useFieldArray({
    control,
    name: "wo_components",
  });

  const {
    fields: compUpdates,
    append: appendCompUpdate,
    remove: removeCompUpdate,
    update: updateCompUpdate,
    replace: replaceCompUpdate,
  } = useFieldArray({
    control,
    name: "wo_updated_components",
  });

  const {
    fields: linkedTGCM,
    append: appendTGCM,
    remove: removeTGCM,
    replace: replaceTGCM,
  } = useFieldArray({
    control,
    name: "pr_linked_tgcm",
  });

  const {
    data: bomRevsResponse,
    isLoading: bomRevsLoading,
    isFetching: bomRevsFetching,
    error: bomRevsError,
    refetch: bomRevsRefetch,
  } = useTSQBOMRevList(
    {
      active_status: "active",
      filters: [
        [
          {
            field: "item_id",
            operator: "==",
            value: watchedBuildItem?.value.id || null,
          },
        ],
      ],
    },
    !!watchedBuildItem,
  );

  const allBomRevOpts: ABomRevOption[] = useMemo(() => {
    if (bomRevsResponse?.items) {
      return bomRevsResponse.items.map((b) => ({
        label: b.version_name,
        type: "bomrev",
        value: {
          id: b.id,
        },
      }));
    } else {
      return [];
    }
  }, [bomRevsResponse]);

  const {
    data: compsResponse,
    isLoading: compsLoading,
    error: compsError,
  } = useTSQBomCompsByRev(
    watchedBomRev?.value.id || "",
    !!watchedBomRev && searchForComps,
  );

  const {
    data: tgcmsResponse,
    isLoading: tgcmsLoading,
    error: tgcmsError,
  } = useTSQTGCMList(
    {
      page: 0,
      page_size: 1000,
      sort_by: "control_modules.number DESC",
      filter:
        formMode === "build"
          ? null
          : `tgcm_events.origin_record_id='${recId}' AND tgcm_events.event_type='BOX'`,
    },
    "work_order_form",
    watchedBuildItem?.value.num === "MSP-1393" && formMode !== "create",
  );

  const [tgcmOpts, setTgcmOpts] = useState<ATGCMOption[]>([]);
  const [tgcmPendingLink, setTgcmPendingLink] = useState<ATGCMOption | null>(
    null,
  );

  const filteredTGCMOpts = useMemo(() => {
    const linkedIds = linkedTGCM.map((l) => l.value.id);
    return tgcmOpts.filter((o) => !linkedIds.includes(o.value.id));
  }, [tgcmOpts, linkedTGCM]);

  // const tgcmOpts: ATGCMOption[] = useMemo(() => {
  //   if (tgcmsResponse?.items) {
  //     return tgcmsResponse.items.map((tgcm) => ({
  //       label: tgcm.number,
  //       type: "genericrecord",
  //       value: {
  //         id: tgcm.id,
  //         table: "control_modules",
  //       },
  //     }));
  //   } else {
  //     return [];
  //   }
  // }, [tgcmsResponse]);

  useEffect(() => {
    refFormMode.current = formMode;
    setValue("form_mode", formMode);
    console.log("REF RENDER", {
      count: ++refRenderCount.current,
      formMode: refFormMode.current,
      origData: refOrigData.current,
      id: id,
      recId: recId,
    });
  });

  const resetForm = useCallback(
    (data: WorkOrderFull | null) => {
      if (data) {
        console.log("Fetched Data", data);
        refOrigData.current = data;
        setOrigWONum(data.number);
        if (
          allItemOpts.length > 0 &&
          allUnitOpts.length > 0 &&
          locOpts.length > 0 &&
          allPalletOpts.length > 0
        ) {
          const updated_components: WOCompEditData[] = [];
          for (let i = 0; i < data.components.length; i++) {
            updated_components.push({});
          }
          const woComps: WOCompData[] = data.components.map((comp) => {
            const item =
              allItemOpts.find((o) => o.value.id === comp.component_item.id) ||
              null;
            const unit =
              allUnitOpts.find((o) => o.value.id === comp.uom_unit.id) || null;
            return {
              line_id: comp.id,
              line: comp.line,
              quantity_per: parseFloat(comp.quantity_per),
              quantity_required: parseFloat(comp.quantity_required),
              quantity_allocated: parseFloat(comp.quantity_allocated),
              quantity_offset: parseFloat(comp.quantity_offset),
              bom_rev_id: comp.bom_rev_id || null,
              notes: comp.notes || null,
              component_item: item,
              uom_unit: unit,
              is_new: false,
              is_delete: false,
            };
          });
          const thisFormData: WOFormData = {
            form_mode: refFormMode.current,
            wo_number: data.number,
            wo_pallet_name: data.pallet_name,
            wo_pallet_lot:
              allPalletOpts.find(
                (o) => o.value.id === data.pallet_work_order_id,
              ) || null,
            wo_is_rework: data.is_rework,
            wo_is_planned_deviation: data.is_planned_deviation,
            wo_is_eng_build: data.is_eng_build,
            wo_is_bioburden: data.is_bioburden,
            wo_item:
              allItemOpts.find((o) => o.value.id === data.item.id) || null,
            wo_uom_unit:
              allUnitOpts.find((o) => o.value.id === data.uom_unit.id) || null,
            wo_quantity: parseFloat(data.quantity),
            wo_location:
              locOpts.find((o) => o.value.id === data.location.id) || null,
            wo_bom_revision: null,
            wo_target_revision: data.target_revision,
            wo_planned_start_date: data.planned_start_date,
            wo_planned_completion_date: data.planned_completion_date,
            wo_notes: data.notes,
            wo_components: woComps,
            wo_updated_components: updated_components,
            pr_completed: !!data.production_review,
            pr_quantity_produced: data.production_review
              ? parseFloat(data.production_review.quantity_produced)
              : refFormMode.current === "build"
                ? parseFloat(data.quantity)
                : 0,
            pr_built_revision: data.production_review
              ? data.production_review.revision_built
              : refFormMode.current === "build"
                ? data.target_revision
                : "",
            pr_actual_start_date: data.production_review
              ? data.production_review.actual_start_date
              : "",
            pr_actual_completion_date: data.production_review
              ? data.production_review.actual_completion_date
              : refFormMode.current === "build"
                ? new Date().toISOString()
                : "",
            pr_expiration_date: data.production_review
              ? data.production_review.expiration_date
              : null,
            pr_right_first_time: data.production_review
              ? data.production_review.right_first_time
              : false,
            pr_rework: data.production_review
              ? data.production_review.rework
              : refFormMode.current === "build"
                ? data.is_rework
                : false,
            pr_planned_deviation: data.production_review
              ? data.production_review.planned_deviation
              : refFormMode.current === "build"
                ? data.is_planned_deviation
                : false,
            pr_notes: data.production_review
              ? data.production_review.notes
              : null,
            pr_linked_tgcm: [],
          };
          setWoFormEdits({});
          reset(thisFormData);
          setEditsDisabled(thisFormData.pr_completed);
          setRecId(data.id);
          console.log("RESETFORM WITH DATA SET RECID", data.id);
        }
      } else {
        reset({
          ...defaultWoFormData,
          wo_location: locOpts.find((o) => o.label === "Omaha") || null,
        });
        refOrigData.current = null;
        setOrigWONum("");
        setEditsDisabled(false);
        setWoFormEdits({});
        setRecId(null);
        console.log("RESETFORM NO DATA SET RECID", null);
      }
      setOverrideTargetRev(false);
      setOverrideWOGen(false);
    },
    [allItemOpts, allUnitOpts, locOpts, allPalletOpts, reset],
  );

  useEffect(() => {
    console.log("BIG USEEFFECT", {
      render: refRenderCount.current,
      formMode: refFormMode.current,
      woData: thisWOData,
    });
    if (refFormMode.current !== "create") {
      if (thisWOData) {
        console.log("RESETTING FORM WITH DATA");
        resetForm(thisWOData);
      }
    } else {
      console.log("RESESTTING FORM FOR CREATE MODE");
      resetForm(null);
    }
  }, [thisWOData, resetForm]);

  useEffect(() => {
    console.log("BOM REV USE EFFECT", {
      formInit: formInit,
      bomOpts: allBomRevOpts,
      woExists: !!thisWOData,
    });
    if (!formInit && !bomRevsFetching && thisWOData) {
      const bom =
        allBomRevOpts.find((o) => o.value.id === thisWOData.bom_revision?.id) ||
        null;
      setValue("wo_bom_revision", bom);
      setFormInit(true);
    }
  }, [thisWOData, allBomRevOpts, bomRevsFetching, formInit, setValue]);

  useEffect(() => {
    if (tgcmsResponse?.items) {
      if (refFormMode.current === "view") {
        const opts: ATGCMOption[] = tgcmsResponse.items.map((tgcm) => ({
          label: tgcm.number,
          type: "record",
          value: {
            id: tgcm.id,
            table: "control_modules",
          },
        }));
        replaceTGCM(opts);
      } else {
        setTgcmOpts(
          tgcmsResponse.items.map((tgcm) => ({
            label: tgcm.number,
            type: "record",
            value: {
              id: tgcm.id,
              table: "control_modules",
            },
          })),
        );
      }
    }
  }, [tgcmsResponse, replaceTGCM]);

  useEffect(() => {
    console.log("BOM COMP USEEFFECT", {
      render: refRenderCount.current,
      formMode: refFormMode.current,
      comps: compsResponse,
      bomRev: watchedBomRev,
    });
    if (
      (refFormMode.current === "create" || refFormMode.current === "edit") &&
      !watchedPRComplete
    ) {
      if (compsResponse?.items && watchedBomRev) {
        const compData: WOCompData[] = compsResponse.items.map((comp) => ({
          line_id: null,
          line: comp.line,
          quantity_per: parseFloat(comp.quantity),
          quantity_offset: parseFloat(comp.offset),
          quantity_allocated: 0,
          quantity_required:
            (parseFloat(comp.quantity) * watchedBuildQuant) /
            parseFloat(comp.offset),
          bom_rev_id: watchedBomRev.value.id,
          notes: comp.notes || null,
          component_item:
            allItemOpts.find((o) => o.value.id === comp.component_item.id) ||
            null,
          uom_unit:
            allUnitOpts.find((o) => o.value.id === comp.uom_unit.id) || null,
          is_new: true,
          is_delete: false,
        }));
        const updated_components: WOCompEditData[] = [];
        for (let i = 0; i < compData.length; i++) {
          updated_components.push({});
        }
        replaceComp(compData);
        replaceCompUpdate(updated_components);
        setSearchForComps(false);
      }
    }
  }, [
    compsResponse,
    watchedBomRev,
    allItemOpts,
    allUnitOpts,
    replaceComp,
    replaceCompUpdate,
    watchedBuildQuant,
    watchedPRComplete,
  ]);

  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/manufacturing/orders/form?mode=view&id=${recId}`);
    setValue("form_mode", "view");
    if (thisWOData) resetForm(thisWOData);
  };

  const switchToEditMode = () => {
    navigate(`/manufacturing/orders/form?mode=edit&id=${recId}`);
    setValue("form_mode", "edit");
    if (thisWOData) resetForm(thisWOData);
  };

  const switchToBuildMode = () => {
    navigate(`/manufacturing/orders/form?mode=build&id=${recId}`);
    setValue("form_mode", "build");
    if (thisWOData) resetForm(thisWOData);
  };

  const switchToCreateMode = () => {
    navigate("/manufacturing/orders/form?mode=create");
    setValue("wo_number", "");
    resetForm(null);
  };

  // Form submission
  const onSubmit = async (data: WOFormData) => {
    try {
      console.log("SUBMIT", {
        render: refRenderCount.current,
        data: data,
      });
      setError(null);
      setProcessDialogOpen(true);
      if (formMode === "create") {
        const woPayload: WorkOrderCreate = {
          number: data.wo_number !== "" ? data.wo_number : null,
          pallet_name: data.wo_pallet_name,

          item_id: data.wo_item!.value.id,
          bom_revision_id: data.wo_bom_revision?.value.id || null,
          target_revision: `${data.wo_target_revision}${
            data.wo_is_eng_build ? "-ENG" : ""
          }`,
          location_id: data.wo_location!.value.id,
          uom_unit_id: data.wo_uom_unit!.value.id,

          pallet_work_order_id: data.wo_pallet_lot?.value.id || null,

          is_rework: data.wo_is_rework,
          is_planned_deviation: data.wo_is_planned_deviation,
          is_eng_build: data.wo_is_eng_build,
          is_bioburden: data.wo_is_bioburden,

          quantity: data.wo_quantity,

          wo_date: new Date().toISOString(),
          planned_start_date: data.wo_planned_start_date,
          planned_completion_date: data.wo_planned_completion_date,

          notes: data.wo_notes,

          component_data: data.wo_components.map((compData, index) => ({
            line: index + 1,
            work_order_id: null,
            component_item_id: compData.component_item!.value.id,
            uom_unit_id: compData.uom_unit!.value.id,
            quantity_per: compData.quantity_per,
            quantity_offset: compData.quantity_offset,
            quantity_required: compData.quantity_required,
            bom_rev_id: compData.bom_rev_id,
            notes: compData.notes,
          })),
        };
        const response = await createWOMutation.mutateAsync(woPayload);
        setTimeout(() => {
          handleProcessDialogClose(null, "create_complete", true, response.id);
        }, 5000);
      } else if (formMode === "edit") {
        if (!recId) {
          throw new Error("Missing record ID");
        }
        const deleteLines: string[] = [];
        const updateLines: WOCompUpdate[] = [];
        const newLines: WOCompCreate[] = [];
        data.wo_components.forEach((compLine, index) => {
          if (compLine.is_new) {
            newLines.push({
              line: index + 1,
              work_order_id: recId,
              component_item_id: compLine.component_item!.value.id,
              uom_unit_id: compLine.uom_unit!.value.id,
              quantity_per: compLine.quantity_per,
              quantity_required: compLine.quantity_required,
              quantity_offset: compLine.quantity_offset,
              bom_rev_id: compLine.bom_rev_id,
              notes: compLine.notes,
            });
          } else if (compLine.is_delete && !!compLine.line_id) {
            deleteLines.push(compLine.line_id);
          } else {
            const lineUpdates = data.wo_updated_components[index];
            updateLines.push({
              line_id: compLine.line_id!,
              component_item_id: lineUpdates.component_item
                ? lineUpdates.component_item.value.id
                : undefined,
              uom_unit_id: lineUpdates.uom_unit
                ? lineUpdates.uom_unit.value.id
                : undefined,
              quantity_per: lineUpdates.quantity_per,
              quantity_required: lineUpdates.quantity_required,
              quantity_allocated: lineUpdates.quantity_allocated,
              quantity_offset: lineUpdates.quantity_offset,
              notes: lineUpdates.notes,
            });
          }
        });
        const woPayload: WorkOrderUpdate = {
          lines_to_update: updateLines,
          lines_to_create: newLines,
          lines_to_delete: deleteLines,
        };
        if (woFormEdits.wo_number) {
          woPayload["number"] = woFormEdits.wo_number;
        }
        if (woFormEdits.wo_pallet_name) {
          woPayload["pallet_name"] = woFormEdits.wo_pallet_name;
        }
        if (woFormEdits.wo_item) {
          woPayload["item_id"] = woFormEdits.wo_item.value.id;
        }
        if (woFormEdits.wo_bom_revision) {
          woPayload["bom_revision_id"] = woFormEdits.wo_bom_revision.value.id;
        }
        if (woFormEdits.wo_target_revision) {
          woPayload["target_revision"] = woFormEdits.wo_target_revision;
        }
        if (woFormEdits.wo_location) {
          woPayload["location_id"] = woFormEdits.wo_location.value.id;
        }
        if (woFormEdits.wo_uom_unit) {
          woPayload["uom_unit_id"] = woFormEdits.wo_uom_unit.value.id;
        }
        if (woFormEdits.wo_pallet_lot) {
          woPayload["pallet_work_order_id"] =
            woFormEdits.wo_pallet_lot.value.id;
        }
        if (woFormEdits.wo_is_rework !== undefined) {
          woPayload["is_rework"] = woFormEdits.wo_is_rework;
        }
        if (woFormEdits.wo_is_planned_deviation !== undefined) {
          woPayload["is_planned_deviation"] =
            woFormEdits.wo_is_planned_deviation;
        }
        if (woFormEdits.wo_is_eng_build !== undefined) {
          woPayload["is_eng_build"] = woFormEdits.wo_is_eng_build;
        }
        if (woFormEdits.wo_is_bioburden !== undefined) {
          woPayload["is_bioburden"] = woFormEdits.wo_is_bioburden;
        }
        if (woFormEdits.wo_quantity) {
          woPayload["quantity"] = woFormEdits.wo_quantity;
        }
        if (woFormEdits.wo_planned_start_date) {
          woPayload["planned_start_date"] = woFormEdits.wo_planned_start_date;
        }
        if (woFormEdits.wo_planned_completion_date) {
          woPayload["planned_completion_date"] =
            woFormEdits.wo_planned_completion_date;
        }
        if (woFormEdits.wo_notes !== undefined) {
          woPayload["notes"] = woFormEdits.wo_notes;
        }
        const response = await updateWOMutation.mutateAsync({
          id: recId,
          data: woPayload,
        });
        setTimeout(() => {
          handleProcessDialogClose(null, "update_complete", true, response.id);
        }, 5000);
      } else if (formMode === "build") {
        if (!recId) {
          throw new Error("Missing record ID");
        }
        const prodRevPayload: ProdRevCreate = {
          work_order_id: recId,
          quantity_produced: data.pr_quantity_produced,
          revision_built: data.pr_built_revision,
          right_first_time: data.pr_right_first_time,
          planned_deviation: data.pr_planned_deviation,
          rework: data.pr_rework,
          actual_start_date: data.pr_actual_start_date,
          actual_completion_date: data.pr_actual_completion_date,
          labor_cost: null,
          overhead_cost: null,
          expiration_date: data.pr_expiration_date,
          notes: data.pr_notes,
          linked_tgcm: data.pr_linked_tgcm.map((tgcm) => tgcm.value.id),
        };
        const response = await buildWOMutation.mutateAsync({
          id: recId,
          data: prodRevPayload,
        });
        setTimeout(() => {
          handleProcessDialogClose(null, "build_complete", true, response.id);
        }, 5000);
      }
    } catch (err: any) {
      console.error("Failed to save wo:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save wo",
      );
    }
  };

  const handleNavToList = () => {
    navigate("/manufacturing/orders");
  };

  const handleFormCancel = () => {
    navigate("/manufacturing/orders");
  };

  const handleRemoveComp = (index: number, data: WOCompData) => {
    if (data.is_new) {
      removeComp(index);
      removeCompUpdate(index);
    } else {
      updateComp(index, { ...data, is_delete: !data.is_delete });
    }
  };

  const [lotLookupDialogOpen, setLotLookupDialogOpen] =
    useState<boolean>(false);
  const handleLotLookupDialogOpen = () => {
    setLotLookupDialogOpen(true);
  };
  const handleLotLookupDialogClose = () => {
    setLotLookupDialogOpen(false);
  };

  const [compDialogOpen, setCompDialogOpen] = useState<boolean>(false);
  const [compDialogData, setCompDialogData] = useState<WOCompData | null>(null);
  const [compDialogEdits, setCompDialogEdits] = useState<WOCompEditData | null>(
    null,
  );
  const [compDialogIndex, setCompDialogIndex] = useState<number>(-1);

  const handleCompDialogOpen = (
    index: number,
    data: WOCompData,
    edits: WOCompEditData | null,
  ) => {
    setCompDialogData(data);
    setCompDialogEdits(edits);
    setCompDialogIndex(index);
    setCompDialogOpen(true);
  };

  const handleCompDialogSubmit = (data: WOCompData, edits: WOCompEditData) => {
    if (data.is_new) {
      if (compDialogIndex === -1) {
        appendComp(data);
        appendCompUpdate({});
      } else {
        updateComp(compDialogIndex, data);
      }
    } else {
      updateCompUpdate(compDialogIndex, edits);
    }
    setCompDialogOpen(false);
    setCompDialogData(null);
    setCompDialogEdits(null);
    setCompDialogIndex(-1);
  };

  const handleCompDialogCancel = () => {
    setCompDialogOpen(false);
    setCompDialogData(null);
    setCompDialogEdits(null);
    setCompDialogIndex(-1);
  };

  const handleCompDialogItemSelect = (
    newValue: AItemOption | null,
  ): boolean => {
    console.log("CHECKING FOR MATCHING COMPONENTS", {
      value: newValue,
      index: compDialogIndex,
    });
    if (!watchedPRComplete) {
      if (newValue !== null) {
        for (let i = 0; i < components.length; i++) {
          if (i === compDialogIndex) {
            continue;
          }
          let compData = components[i];
          console.log(`CHECKING COMP WITH INDEX ${i}`, compData);
          if (compData.component_item?.value.id === newValue.value.id) {
            return false;
          }
        }
      }
      return true;
    } else {
      setError(
        "SHOULDN'T BE UPDATING A COMPONENT LINE ITEM ON A BUILT WORK ORDER >:(",
      );
      return false;
    }
  };

  const [processDialogOpen, setProcessDialogOpen] = useState<boolean>(false);

  const handleProcessDialogClose = (
    event: any,
    reason: string,
    navToView: boolean,
    recordId: string,
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setProcessDialogOpen(false);
    console.log("Process Dialog Closed", navToView, recordId);
    if (navToView) {
      window.open(
        `/manufacturing/orders/form?mode=view&id=${recordId}`,
        "_self",
      );
    }
  };

  const completeOrder = async () => {
    if (!recId) {
      setError("Missing ID, Cannot Complete Order");
      return;
    }
    try {
      await completeWOMutation.mutateAsync(recId);
      thisWORefetch();
    } catch (e: any) {
      setError("There was an error marking WO Complete");
      console.error(e);
      return;
    }
  };

  const voidOrder = async () => {
    if (!recId) {
      setError("Missing ID, Cannot VOID Order");
      return;
    }
    try {
      await voidWOMutation.mutateAsync(recId);
      thisWORefetch();
    } catch (e: any) {
      setError("There was an error marking WO VOID");
      console.error(e);
      return;
    }
  };

  const handleItemChange = (newValue: AItemOption | null) => {
    if (!watchedPRComplete) {
      // setSelectedBuildItem(newValue);
      const unit =
        allUnitOpts.find(
          (o) =>
            o.value.type_id === newValue?.value.type_id &&
            o.value.conversion === 1,
        ) || null;
      setValue("wo_uom_unit", unit);
      // setSelectedBuildUnit(unit);
      setValue("wo_bom_revision", null);
      if (!overrideTargetRev) {
        setValue("wo_target_revision", "");
      }
      // setSelectedBomRev(null);
      if (formMode === "create") {
        setWoFormEdits((prev) => ({
          ...prev,
          wo_updated_components: [],
        }));
        replaceComp([]);
      }
    } else {
      setError("SHOULDN'T BE UPDATING THE ITEM ON A BUILT WORK ORDER >:(");
    }
  };

  const handleBomRevChange = (newValue: ABomRevOption | null) => {
    if (!watchedPRComplete) {
      if (newValue === null) {
        // setSelectedBomRev(null);
        setSearchForComps(false);
        setWoFormEdits((prev) => ({
          ...prev,
          wo_bom_revision: newValue,
        }));
        replaceComp([]);
        replaceCompUpdate([]);
        if (!overrideTargetRev) {
          setValue("wo_target_revision", "");
        }
      } else {
        // setSelectedBomRev(newValue);
        setSearchForComps(true);
        setWoFormEdits((prev) => ({
          ...prev,
          wo_bom_revision: newValue,
        }));
        replaceComp([]);
        replaceCompUpdate([]);
        if (!overrideTargetRev) {
          setValue(
            "wo_target_revision",
            newValue.label.replace(/[A-Z]{3}[-][\d]{4}[\s][Rr][Ee][Vv]/g, ""),
          );
        }
      }
    } else {
      setError("SHOULDN'T BE UPDATING THE BOM ON A BUILT WORK ORDER >:(");
    }
  };

  const handleBuildQuantChange = (newValue: number) => {
    if (!watchedPRComplete) {
      for (let i = 0; i < components.length; i++) {
        let compData = components[i];
        let uCompData = compUpdates[i];
        if (compData.is_new) {
          updateComp(i, {
            ...compData,
            quantity_required:
              (newValue * compData.quantity_per) / compData.quantity_offset,
          });
        } else {
          updateCompUpdate(i, {
            ...uCompData,
            quantity_required:
              (newValue * (uCompData.quantity_per || compData.quantity_per)) /
              (uCompData.quantity_offset || compData.quantity_offset),
          });
        }
      }
    } else {
      setError(
        "SHOULDN'T BE UPDATING THE TARGET BUILD QUANTITY ON A BUILT WORK ORDER >:(",
      );
    }
  };

  if (formMode === "edit" && watchedPRComplete) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Alert severity="error">
          Cannot Edit Already Built Work Orders... If a Built Work Order
          requires updates, notify IT
        </Alert>
      </Box>
    );
  }

  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Alert severity="info">Loading Data...</Alert>
        <CircularProgress />
      </Box>
    );
  }

  if (!formInit) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Alert severity="info">Preparing Form...</Alert>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="column" gap={1} sx={{ p: 3 }}>
        <Typography variant="h4" component="h1">
          {formMode === "create"
            ? "Create Work Order"
            : `${stringUtils.capFirstLetter(formMode)} WO ${
                thisWOData?.number || "UNKOWN"
              }${
                thisWOData?.pallet_name
                  ? " Pallet: " + thisWOData.pallet_name
                  : ""
              }`}
        </Typography>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleNavToList}
            variant="outlined"
          >
            To Work Orders List
          </Button>
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToCreateMode}
              startIcon={<AddIcon />}
            >
              Create New
            </Button>
          )}
          {(formMode === "view" || formMode === "edit") &&
            recId &&
            refOrigData.current && (
              <OrderStatusActions
                recordType={"work_order"}
                recordId={recId}
                disabled={false}
                orderStatus={refOrigData.current.order_status}
                approvalStatus={refOrigData.current.approval_status}
                woStatus={refOrigData.current.build_status}
                mutation={statusUpdateMutation}
                onStatusUpdateComplete={(response) => {
                  if (response.success) {
                    window.open(
                      `/manufacturing/orders/form?mode=view&id=${recId}`,
                      "_self",
                    );
                  } else {
                    setError(response.message);
                  }
                }}
              />
            )}
          {formMode === "view" && !editsDisabled && (
            <Button
              variant="outlined"
              onClick={switchToEditMode}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          {formMode === "edit" && (
            <Button
              variant="outlined"
              onClick={switchToViewMode}
              startIcon={<VisibilityIcon />}
            >
              View
            </Button>
          )}
          {formMode === "view" && !watchedPRComplete && (
            <Button
              variant="outlined"
              onClick={switchToBuildMode}
              startIcon={<AddIcon />}
            >
              Build
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              console.log({
                formData: getValues(),
                editData: woFormEdits,
                origData: refOrigData.current,
              });
            }}
          >
            DEBUG PRINT
          </Button>
        </Box>

        {/* Error Alert */}
        {(error ||
          locsError ||
          thisWOError ||
          itemsError ||
          bomRevsError ||
          unitsError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              itemsError?.message ||
              unitsError?.message ||
              thisWOError?.message ||
              bomRevsError?.message ||
              locsError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {formMode === "build" &&
          watchedPalletLot?.value.build_complete === false && (
            <Alert severity="error">Pallet Build is Not Completed</Alert>
          )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <Stack spacing={3} sx={{ width: "100%" }}>
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleFormCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={
                      isSubmitting ||
                      editsDisabled ||
                      (formMode === "build" &&
                        watchedPalletLot?.value.build_complete === false)
                    }
                  >
                    {isSubmitting
                      ? "Saving..."
                      : formMode === "create"
                        ? "Create Work Order"
                        : `${
                            formMode === "build" ? "Build" : "Update"
                          } Work Order`}
                  </Button>
                </Box>
              )}
              {formMode !== "create" && thisWOData && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="h6">Order Status:</Typography>
                  <Chip
                    clickable={false}
                    color={statusUtils.getPrimaryOrderStatusColor(
                      thisWOData.order_status,
                    )}
                    label={stringUtils.capFirstLetter(thisWOData.order_status)}
                    size="small"
                  />
                  <Typography variant="h6">Build Status:</Typography>
                  <Chip
                    clickable={false}
                    color={statusUtils.getWoBuildStatusColor(
                      thisWOData.build_status,
                    )}
                    label={stringUtils.capFirstLetter(thisWOData.build_status)}
                    size="small"
                  />
                  <Typography variant="h6">Part Status:</Typography>
                  <Chip
                    clickable={false}
                    color={statusUtils.getWoPartStatusColor(
                      thisWOData.part_status,
                    )}
                    label={stringUtils.capFirstLetter(thisWOData.part_status)}
                    size="small"
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Stack
                  sx={{
                    direction: "column",
                    gap: 2,
                    width: "50%",
                  }}
                >
                  <Typography variant="h6">Basic Information</Typography>
                  <Controller
                    name="wo_number"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <TextField
                        {...restField}
                        value={
                          woFormEdits.wo_number === undefined
                            ? value
                            : woFormEdits.wo_number
                        }
                        onChange={(e) => {
                          if (formMode === "edit") {
                            setWoFormEdits((prev) => ({
                              ...prev,
                              wo_number: e.target.value,
                            }));
                          } else {
                            onChange(e);
                          }
                        }}
                        label="WO Number"
                        fullWidth
                        disabled={
                          !overrideWOGen ||
                          editsDisabled ||
                          formMode === "view" ||
                          formMode === "build"
                        }
                        error={!!errors.wo_number}
                        helperText={
                          errors.wo_number?.message || formMode === "view"
                            ? undefined
                            : "WO Numbers must be unique."
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip
                                  title={
                                    overrideWOGen
                                      ? "Lock WO Number"
                                      : formMode === "create"
                                        ? "Override WO# Generation"
                                        : "Update WO#"
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      setOverrideWOGen(!overrideWOGen)
                                    }
                                    edge="end"
                                    size="small"
                                    disabled={
                                      editsDisabled ||
                                      formMode === "view" ||
                                      formMode === "build"
                                    }
                                  >
                                    {overrideWOGen ? (
                                      <LockIcon />
                                    ) : (
                                      <EditIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset WO#">
                                  <IconButton
                                    onClick={() => {
                                      if (overrideWOGen)
                                        setOverrideWOGen(false);
                                      setValue("wo_number", origWONum);
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode === "view" ||
                                      formMode === "build" ||
                                      origWONum === watchedWONum ||
                                      editsDisabled
                                    }
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="wo_location"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          fullWidth
                          value={
                            woFormEdits.wo_location === undefined
                              ? value
                              : woFormEdits.wo_location
                          }
                          onChange={(event, newValue) => {
                            if (formMode === "edit") {
                              setWoFormEdits((prev) => ({
                                ...prev,
                                wo_location: newValue,
                              }));
                            } else {
                              onChange(newValue);
                            }
                          }}
                          options={locOpts}
                          disabled={
                            formMode === "view" ||
                            formMode === "build" ||
                            locOpts.length === 0 ||
                            editsDisabled
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value?.value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Location"
                              error={!!errors.wo_location}
                              helperText={errors.wo_location?.message}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                locOpts.length === 0 ||
                                editsDisabled
                              }
                            />
                          )}
                        />
                      );
                    }}
                  />
                </Stack>
                <Stack
                  sx={{
                    direction: "column",
                    gap: 2,
                    width: "50%",
                  }}
                >
                  <Typography variant="h6">Planned Dates</Typography>
                  <Controller
                    name="wo_planned_start_date"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <DatePicker
                        {...restField}
                        label="Start"
                        value={
                          woFormEdits.wo_planned_start_date === undefined
                            ? new Date(value)
                            : new Date(woFormEdits.wo_planned_start_date)
                        }
                        onChange={(date) => {
                          if (formMode === "edit") {
                            setWoFormEdits((prev) => ({
                              ...prev,
                              wo_planned_start_date: date?.toISOString(),
                            }));
                          } else {
                            onChange(date?.toISOString());
                          }
                        }}
                        disabled={
                          formMode === "view" ||
                          formMode === "build" ||
                          editsDisabled
                        }
                        slotProps={{
                          textField: {
                            error: !!errors.wo_planned_start_date,
                            helperText: errors.wo_planned_start_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="wo_planned_completion_date"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <DatePicker
                        {...restField}
                        label="Completion"
                        value={
                          woFormEdits.wo_planned_completion_date === undefined
                            ? new Date(value)
                            : new Date(woFormEdits.wo_planned_completion_date)
                        }
                        onChange={(date) => {
                          if (formMode === "edit") {
                            setWoFormEdits((prev) => ({
                              ...prev,
                              wo_planned_completion_date: date?.toISOString(),
                            }));
                          } else {
                            onChange(date?.toISOString());
                          }
                        }}
                        disabled={
                          formMode === "view" ||
                          formMode === "build" ||
                          editsDisabled
                        }
                        slotProps={{
                          textField: {
                            error: !!errors.wo_planned_completion_date,
                            helperText:
                              errors.wo_planned_completion_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Stack direction="column" gap={1} width="50%">
                  <Typography variant="h6" gutterBottom>
                    Build Flags
                  </Typography>
                  <Stack direction="column" gap={1} alignItems={"start"}>
                    <Controller
                      name="wo_is_rework"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                woFormEdits.wo_is_rework === undefined
                                  ? value
                                  : woFormEdits.wo_is_rework
                              }
                              onChange={(e, c) => {
                                if (formMode === "edit") {
                                  setWoFormEdits((prev) => ({
                                    ...prev,
                                    wo_is_rework: c,
                                  }));
                                } else {
                                  onChange(c);
                                }
                              }}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                editsDisabled
                              }
                            />
                          }
                          label="Rework?"
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      name="wo_is_planned_deviation"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                woFormEdits.wo_is_planned_deviation ===
                                undefined
                                  ? value
                                  : woFormEdits.wo_is_planned_deviation
                              }
                              onChange={(e, c) => {
                                if (formMode === "edit") {
                                  setWoFormEdits((prev) => ({
                                    ...prev,
                                    wo_is_planned_deviation: c,
                                  }));
                                } else {
                                  onChange(c);
                                }
                              }}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                editsDisabled
                              }
                            />
                          }
                          label="Planned Deviation?"
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      name="wo_is_eng_build"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                woFormEdits.wo_is_eng_build === undefined
                                  ? value
                                  : woFormEdits.wo_is_eng_build
                              }
                              onChange={(e, c) => {
                                if (formMode === "edit") {
                                  setWoFormEdits((prev) => ({
                                    ...prev,
                                    wo_is_eng_build: c,
                                  }));
                                } else {
                                  onChange(c);
                                }
                              }}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                editsDisabled
                              }
                            />
                          }
                          label="Engineering Build?"
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      name="wo_is_bioburden"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                woFormEdits.wo_is_bioburden === undefined
                                  ? value
                                  : woFormEdits.wo_is_bioburden
                              }
                              onChange={(e, c) => {
                                if (formMode === "edit") {
                                  setWoFormEdits((prev) => ({
                                    ...prev,
                                    wo_is_bioburden: c,
                                  }));
                                } else {
                                  onChange(c);
                                }
                              }}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                editsDisabled
                              }
                            />
                          }
                          label="BioBurden?"
                          labelPlacement="start"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    direction: "column",
                    gap: 2,
                    width: "50%",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Item Information
                  </Typography>
                  <Controller
                    name="wo_item"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          fullWidth
                          value={
                            woFormEdits.wo_item === undefined
                              ? value
                              : woFormEdits.wo_item
                          }
                          onChange={(event, newValue) => {
                            if (formMode === "edit") {
                              setWoFormEdits((prev) => ({
                                ...prev,
                                wo_item: newValue,
                              }));
                            } else {
                              onChange(newValue);
                            }
                            handleItemChange(newValue);
                          }}
                          options={allItemOpts}
                          disabled={
                            formMode === "view" ||
                            formMode === "build" ||
                            editsDisabled
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value?.value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Item"
                              error={!!errors.wo_item}
                              helperText={errors.wo_item?.message}
                              disabled={
                                formMode === "view" ||
                                formMode === "build" ||
                                editsDisabled
                              }
                            />
                          )}
                        />
                      );
                    }}
                  />
                  {["MSP-1782", "MSP-2388"].includes(
                    watchedBuildItem?.value.num || "",
                  ) && (
                    <Controller
                      name="wo_pallet_name"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <TextField
                          {...restField}
                          value={
                            woFormEdits.wo_pallet_name === undefined
                              ? value
                              : woFormEdits.wo_pallet_name
                          }
                          onChange={(e) => {
                            if (formMode === "edit") {
                              setWoFormEdits((prev) => ({
                                ...prev,
                                wo_pallet_name: e.target.value,
                              }));
                              if (value === null) {
                                onChange(e);
                              }
                            } else {
                              onChange(e);
                            }
                          }}
                          disabled={
                            formMode === "view" ||
                            formMode === "build" ||
                            editsDisabled
                          }
                          fullWidth
                          label="Pallet Name"
                          error={!!errors.wo_pallet_name}
                          helperText={errors.wo_pallet_name?.message}
                        />
                      )}
                    />
                  )}
                  {watchedBuildItem?.value.num.startsWith("FGS") && (
                    <Controller
                      name="wo_pallet_lot"
                      control={control}
                      render={({
                        field: { onChange, value, ...restField },
                      }) => {
                        return (
                          <Autocomplete
                            {...restField}
                            fullWidth
                            options={allPalletOpts}
                            value={
                              woFormEdits.wo_pallet_lot === undefined
                                ? value
                                : woFormEdits.wo_pallet_lot
                            }
                            onChange={(event, newValue) => {
                              if (formMode === "edit") {
                                setWoFormEdits((prev) => ({
                                  ...prev,
                                  wo_pallet_lot: newValue,
                                }));
                                if (value === null) {
                                  onChange(newValue);
                                }
                              } else {
                                onChange(newValue);
                              }
                            }}
                            disabled={
                              formMode === "view" ||
                              formMode === "build" ||
                              editsDisabled
                            }
                            groupBy={(opt) => opt.group || ""}
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value?.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Pallet Lot"
                                error={!!errors.wo_pallet_lot}
                                helperText={errors.wo_pallet_lot?.message}
                                disabled={
                                  formMode === "view" ||
                                  formMode === "build" ||
                                  editsDisabled
                                }
                              />
                            )}
                          />
                        );
                      }}
                    />
                  )}

                  <Box display="flex" gap={2}>
                    <Controller
                      name="wo_bom_revision"
                      control={control}
                      render={({
                        field: { onChange, value, ...restField },
                      }) => {
                        return (
                          <Autocomplete
                            {...restField}
                            fullWidth
                            value={
                              woFormEdits.wo_bom_revision === undefined
                                ? value
                                : woFormEdits.wo_bom_revision
                            }
                            onChange={(event, newValue) => {
                              if (formMode === "edit") {
                                setWoFormEdits((prev) => ({
                                  ...prev,
                                  wo_bom_revision: newValue,
                                }));
                              } else {
                                onChange(newValue);
                              }
                              handleBomRevChange(newValue);
                            }}
                            options={allBomRevOpts}
                            disabled={
                              formMode === "view" ||
                              formMode === "build" ||
                              editsDisabled ||
                              allBomRevOpts.length === 0 ||
                              !watchedBuildItem
                            }
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value?.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="BOM Revision"
                                error={!!errors.wo_bom_revision}
                                helperText={
                                  errors.wo_bom_revision?.message ||
                                  formMode === "create"
                                    ? "Changing BomRev will update component list"
                                    : formMode === "edit"
                                      ? "Changing BomRev will NOT update component list"
                                      : undefined
                                }
                                disabled={
                                  formMode === "view" ||
                                  formMode === "build" ||
                                  editsDisabled ||
                                  allBomRevOpts.length === 0
                                }
                              />
                            )}
                          />
                        );
                      }}
                    />
                    <Controller
                      name="wo_target_revision"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <TextField
                          {...restField}
                          value={
                            woFormEdits.wo_target_revision === undefined
                              ? value
                              : woFormEdits.wo_target_revision
                          }
                          onChange={(e) => {
                            if (formMode === "edit") {
                              setWoFormEdits((prev) => ({
                                ...prev,
                                wo_target_revision: e.target.value,
                              }));
                            } else {
                              onChange(e.target.value);
                            }
                          }}
                          label="Target Revision"
                          fullWidth
                          disabled={
                            formMode === "view" ||
                            formMode === "build" ||
                            editsDisabled ||
                            !overrideTargetRev
                          }
                          error={!!errors.wo_target_revision}
                          helperText={errors.wo_target_revision?.message}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {watchedEngBuild && "-ENG"}
                                  <Tooltip
                                    title={
                                      overrideTargetRev
                                        ? "Lock Target to Bom Rev"
                                        : "Set Target Manually"
                                    }
                                  >
                                    <IconButton
                                      disabled={
                                        formMode === "view" ||
                                        formMode === "build" ||
                                        editsDisabled
                                      }
                                      onClick={() => {
                                        if (overrideTargetRev) {
                                          setValue(
                                            "wo_target_revision",
                                            watchedBomRev
                                              ? watchedBomRev.label.replace(
                                                  `${watchedBuildItem?.value.num} Rev`,
                                                  "",
                                                )
                                              : "",
                                          );
                                        } else {
                                          setFocus("wo_target_revision");
                                        }
                                        setOverrideTargetRev(
                                          !overrideTargetRev,
                                        );
                                      }}
                                      edge="end"
                                      size="small"
                                    >
                                      {overrideTargetRev ? (
                                        <LockIcon />
                                      ) : (
                                        <EditIcon />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box display="flex" gap={2}>
                    <Controller
                      name="wo_quantity"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <NumericEntryField
                          value={
                            woFormEdits.wo_quantity === undefined
                              ? value
                              : woFormEdits.wo_quantity
                          }
                          onChange={(v) => {
                            if (formMode === "edit") {
                              setWoFormEdits((prev) => ({
                                ...prev,
                                wo_quantity: v,
                              }));
                            } else {
                              onChange(v);
                            }
                            handleBuildQuantChange(v);
                          }}
                          isCurrency={false}
                          decimalPlaces={2}
                          label="Quantity To Build"
                          disabled={
                            formMode === "view" ||
                            formMode === "build" ||
                            editsDisabled
                          }
                          textFieldProps={{
                            ...restField,
                            fullWidth: true,
                            error: !!errors.wo_quantity,
                            helperText: errors.wo_quantity?.message,
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="wo_uom_unit"
                      control={control}
                      render={({
                        field: { onChange, value, ...restField },
                      }) => {
                        const availOpts = allUnitOpts.filter(
                          (o) =>
                            o.value.type_id === watchedBuildItem?.value.type_id,
                        );
                        return (
                          <Autocomplete
                            {...restField}
                            fullWidth
                            value={
                              woFormEdits.wo_uom_unit === undefined
                                ? value
                                : woFormEdits.wo_uom_unit
                            }
                            onChange={(event, newValue) => {
                              if (formMode === "edit") {
                                setWoFormEdits((prev) => ({
                                  ...prev,
                                  wo_uom_unit: newValue,
                                }));
                              } else {
                                onChange(newValue);
                              }
                            }}
                            options={availOpts}
                            disabled={
                              formMode === "view" ||
                              formMode === "build" ||
                              editsDisabled ||
                              availOpts.length === 0
                            }
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value?.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="UOM"
                                error={!!errors.wo_uom_unit}
                                helperText={errors.wo_uom_unit?.message}
                                disabled={
                                  formMode === "view" ||
                                  formMode === "build" ||
                                  editsDisabled ||
                                  availOpts.length === 0
                                }
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              {(formMode === "build" || watchedPRComplete) && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Build Information
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Stack direction="column" gap={2} width="50%">
                      <Typography variant="h6">Actual Dates</Typography>
                      <Controller
                        name="pr_actual_start_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            label="Start"
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date) =>
                              field.onChange(date?.toISOString())
                            }
                            disabled={formMode === "view" || editsDisabled}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.pr_actual_start_date,
                                helperText:
                                  errors.pr_actual_start_date?.message,
                              },
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="pr_actual_completion_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            label="Completion"
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date) =>
                              field.onChange(date?.toISOString() || "")
                            }
                            disabled={formMode === "view" || editsDisabled}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.pr_actual_completion_date,
                                helperText:
                                  errors.pr_actual_completion_date?.message,
                              },
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="pr_expiration_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            label="Expiration Date"
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date) =>
                              field.onChange(date?.toISOString())
                            }
                            disabled={
                              formMode === "view" ||
                              editsDisabled ||
                              watchedBuildItem?.value.expires === false
                            }
                            slotProps={{
                              textField: {
                                error: !!errors.pr_expiration_date,
                                helperText: errors.pr_expiration_date?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </Stack>
                    <Stack direction="column" gap={2} width="50%">
                      <Typography variant="h6">Production Details</Typography>
                      <Box display="flex" gap={0.5} alignItems={"center"}>
                        <Controller
                          name="pr_quantity_produced"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <NumericEntryField
                              value={value}
                              onChange={(v) => onChange(v)}
                              isCurrency={false}
                              decimalPlaces={2}
                              label="Quantity Built"
                              disabled={formMode === "view" || editsDisabled}
                              textFieldProps={{
                                ...restField,
                                fullWidth: true,
                                error: !!errors.pr_quantity_produced,
                                helperText:
                                  errors.pr_quantity_produced?.message,
                              }}
                            />
                          )}
                        />
                        <Typography variant="body1">
                          {watchedBuildUnit
                            ? `x ${watchedBuildUnit.label}`
                            : ""}
                        </Typography>
                      </Box>

                      <Controller
                        name="pr_built_revision"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Revision Built"
                            fullWidth
                            disabled={formMode === "view" || editsDisabled}
                            error={!!errors.pr_built_revision}
                            helperText={errors.pr_built_revision?.message}
                          />
                        )}
                      />
                      {watchedBuildItem?.value.num === "MSP-1393" && (
                        <Stack direction="column" gap={1}>
                          <Typography variant="h6">Linked TGCM</Typography>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={1}
                            width="100%"
                            border={"black 1px solid"}
                            minHeight={50}
                            sx={{
                              background: "#a6e3ffff",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                            alignItems={"flex-start"}
                            p={2}
                          >
                            {linkedTGCM.map((tgcm, index) => (
                              <Chip
                                key={index}
                                label={tgcm.label}
                                color="default"
                                sx={{ flexShrink: 0 }}
                                onDelete={
                                  formMode === "build"
                                    ? (e) => {
                                        removeTGCM(index);
                                      }
                                    : undefined
                                }
                              />
                            ))}
                          </Box>
                          {!!errors.pr_linked_tgcm?.message && (
                            <Alert severity="error">
                              {errors.pr_linked_tgcm.message}
                            </Alert>
                          )}
                          {formMode === "build" && (
                            <Box display="flex" gap={0.5}>
                              <Autocomplete
                                fullWidth
                                value={tgcmPendingLink}
                                options={filteredTGCMOpts}
                                onChange={(e, v) => setTgcmPendingLink(v)}
                                getOptionLabel={(opt) => opt?.label || ""}
                                isOptionEqualToValue={(opt, val) =>
                                  opt.value.id === val.value.id
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label="Link TGCM" />
                                )}
                              />
                              <Button
                                variant="outlined"
                                color="info"
                                onClick={() => {
                                  if (tgcmPendingLink !== null) {
                                    appendTGCM(tgcmPendingLink);
                                    setTgcmPendingLink(null);
                                  }
                                }}
                                disabled={!tgcmPendingLink}
                                sx={{ width: "15%" }}
                              >
                                Add TGCM
                              </Button>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>

                  <Box display="flex" gap={2}>
                    <Controller
                      name="pr_right_first_time"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...restField}
                              size="small"
                              checked={value}
                              onChange={onChange}
                              disabled={formMode === "view" || editsDisabled}
                            />
                          }
                          label="Right First Time?"
                        />
                      )}
                    />
                    <Controller
                      name="pr_planned_deviation"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...restField}
                              size="small"
                              checked={value}
                              onChange={onChange}
                              disabled={formMode === "view" || editsDisabled}
                            />
                          }
                          label="Planned Deviation?"
                        />
                      )}
                    />
                    <Controller
                      name="pr_rework"
                      control={control}
                      render={({
                        field: { value, onChange, ...restField },
                      }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...restField}
                              size="small"
                              checked={value}
                              onChange={onChange}
                              disabled={formMode === "view" || editsDisabled}
                            />
                          }
                          label="Rework?"
                        />
                      )}
                    />
                  </Box>
                </>
              )}
              {formMode === "build" && !watchedPRComplete && (
                <>
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={() => handleLotLookupDialogOpen()}
                    sx={{ width: 300 }}
                  >
                    Open Lot Lookup Dialog
                  </Button>
                  <LotLookupDialog
                    open={lotLookupDialogOpen}
                    onClose={handleLotLookupDialogClose}
                  />
                </>
              )}

              {(formMode === "create" || formMode === "edit") &&
                !editsDisabled && (
                  <Button
                    variant="contained"
                    color="info"
                    onClick={() =>
                      handleCompDialogOpen(-1, defaultCompData, null)
                    }
                    sx={{ maxWidth: 300 }}
                    startIcon={<AddIcon />}
                    disabled={!watchedBuildItem}
                  >
                    Add New Component
                  </Button>
                )}

              <TableContainer component={Paper} sx={{ mb: 2, border: "black" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: "5%", maxWidth: "5%" }}>
                        Row #
                      </TableCell>
                      <TableCell sx={{ minWidth: "10%", maxWidth: "10%" }}>
                        Item
                      </TableCell>
                      <TableCell sx={{ minWidth: "40%", maxWidth: "40%" }}>
                        Need Quantity
                      </TableCell>
                      <TableCell sx={{ minWidth: "10%", maxWidth: "10%" }}>
                        Allocated Quantity
                      </TableCell>
                      <TableCell sx={{ minWidth: "25%", maxWidth: "30%" }}>
                        Notes
                      </TableCell>
                      {(formMode === "create" || formMode === "edit") && (
                        <TableCell sx={{ width: "10%" }}></TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} align="left">
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <StarIcon color="success" />
                          <Typography variant="h5">New Line</Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <DeleteIcon color="error" />
                          <Typography variant="h5">
                            Line Pending Deletion
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <EditIcon color="info" />
                          <Typography variant="h5">
                            Value Pending Update
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {components.map((comp, index) => (
                      <WOCompLine
                        key={comp.id}
                        index={index}
                        formMode={formMode}
                        compData={comp}
                        compEdits={compUpdates[index]}
                        buildItem={watchedBuildItem}
                        buildUnit={watchedBuildUnit}
                        handleRemoveComp={handleRemoveComp}
                        handleEditComp={handleCompDialogOpen}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <CompDialog
                open={compDialogOpen}
                buildQuant={watchedBuildQuant}
                itemOpts={allItemOpts.filter(
                  (o) =>
                    o.value.id !== watchedBuildItem?.value.id &&
                    (() => {
                      let good = true;
                      for (let i = 0; i < components.length; i++) {
                        if (i === compDialogIndex) {
                          continue;
                        } else {
                          good =
                            components[i].component_item?.value.id !==
                            o.value.id;
                        }
                      }
                      return good;
                    }),
                )}
                unitOpts={allUnitOpts}
                prevCompData={compDialogData}
                prevCompEdits={compDialogEdits}
                onSubmit={handleCompDialogSubmit}
                onCancel={handleCompDialogCancel}
                onCompItemSelect={handleCompDialogItemSelect}
              />

              <Dialog
                open={processDialogOpen}
                onClose={(e, r) => {
                  return;
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Request Processing Status</DialogTitle>
                <DialogContent>
                  {formMode === "create" ? (
                    <Stack direction="column" gap={3} p={1}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">
                          Creating Work Order
                        </Typography>
                        {createWOMutation.isPending ? (
                          <CircularProgress />
                        ) : createWOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {createWOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  ) : formMode === "edit" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">
                          Updating Work Order
                        </Typography>
                        {updateWOMutation.isPending ? (
                          <CircularProgress />
                        ) : updateWOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {updateWOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">
                          Building Work Order
                        </Typography>
                        {buildWOMutation.isPending ? (
                          <CircularProgress />
                        ) : buildWOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {buildWOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  )}
                </DialogContent>
              </Dialog>

              {/* Form Actions */}
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleFormCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={
                      isSubmitting ||
                      editsDisabled ||
                      (formMode === "build" &&
                        watchedPalletLot?.value.build_complete === false)
                    }
                  >
                    {isSubmitting
                      ? "Saving..."
                      : formMode === "create"
                        ? "Create Work Order"
                        : `${
                            formMode === "build" ? "Build" : "Update"
                          } Work Order`}
                  </Button>
                </Box>
              )}
            </Stack>
          </form>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
};
